/*
 * MERELDON sticky add-to-cart bar.
 *
 * The bar is a remote control for the main product form rendered by sections/main-product:
 *  - its button clicks the main "Add to cart" button (same submit, cart drawer, errors, analytics);
 *  - its size menu (opens upwards) changes the main variant picker (same price/URL/media/availability updates);
 *  - everything it shows (label, disabled/loading state, price, options, errors) is read back
 *    from the main form, so the two can never disagree.
 * It appears once the main button has been scrolled above the viewport and hides again when the
 * main button is back in view, the same pattern Princess Polly uses.
 */
(() => {
  if (customElements.get('mereldon-sticky-atc')) return;

  const DESKTOP_QUERY = '(min-width: 750px)';
  const instances = new Set();

  const cleanText = (node) => (node ? node.textContent.replace(/\s+/g, ' ').trim() : '');

  class MereldonStickyAtc extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
      this.onProductInfoLoaded = this.onProductInfoLoaded.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.onFormChange = this.onFormChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onToggleClick = this.onToggleClick.bind(this);
      this.onToggleKeydown = this.onToggleKeydown.bind(this);
      this.onListClick = this.onListClick.bind(this);
      this.onListKeydown = this.onListKeydown.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onOptionFocusOut = this.onOptionFocusOut.bind(this);
    }

    connectedCallback() {
      // Never run inside a quick-add modal or other copies of the product page.
      if (this.closest('quick-add-modal, .quick-add-modal, product-modal')) return;

      this.active = true;
      this.sourceAbove = false;
      this.isShown = false;

      this.button = this.querySelector('[data-sticky-button]');
      this.label = this.querySelector('[data-sticky-label]');
      this.buttonPrice = this.querySelector('[data-sticky-button-price]');
      this.priceCurrent = this.querySelector('[data-sticky-price-current]');
      this.priceCompare = this.querySelector('[data-sticky-price-compare]');
      this.priceWrap = this.querySelector('[data-sticky-price]');
      this.optionWrap = this.querySelector('[data-sticky-option]');
      this.optionLabel = this.querySelector('[data-sticky-option-label]');
      this.toggle = this.querySelector('[data-sticky-toggle]');
      this.current = this.querySelector('[data-sticky-current]');
      this.list = this.querySelector('[data-sticky-list]');
      this.listOpen = false;
      this.activeIndex = -1;
      this.errorEl = this.querySelector('[data-sticky-error]');
      this.image = this.querySelector('[data-sticky-image]');

      this.button.addEventListener('click', this.onClick);
      this.toggle.addEventListener('click', this.onToggleClick);
      this.toggle.addEventListener('keydown', this.onToggleKeydown);
      this.list.addEventListener('click', this.onListClick);
      this.list.addEventListener('keydown', this.onListKeydown);
      this.optionWrap.addEventListener('focusout', this.onOptionFocusOut);
      document.addEventListener('product-info:loaded', this.onProductInfoLoaded);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      window.addEventListener('pageshow', this.onScroll);

      this.mediaQuery = window.matchMedia(DESKTOP_QUERY);
      this.mediaQuery.addEventListener('change', this.onMediaChange);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateHeight());
        this.resizeObserver.observe(this);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      instances.add(this);
      document.body.classList.add('mrd-has-sticky-atc');

      this.bind();
      this.updateHeight();
    }

    disconnectedCallback() {
      if (!this.active) return;
      this.active = false;

      this.unbindSource();
      this.closeList(false);
      this.button.removeEventListener('click', this.onClick);
      this.toggle.removeEventListener('click', this.onToggleClick);
      this.toggle.removeEventListener('keydown', this.onToggleKeydown);
      this.list.removeEventListener('click', this.onListClick);
      this.list.removeEventListener('keydown', this.onListKeydown);
      this.optionWrap.removeEventListener('focusout', this.onOptionFocusOut);
      document.removeEventListener('product-info:loaded', this.onProductInfoLoaded);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('pageshow', this.onScroll);
      this.mediaQuery.removeEventListener('change', this.onMediaChange);
      this.resizeObserver?.disconnect();
      this.unsubscribeVariant?.();
      cancelAnimationFrame(this.syncFrame);
      cancelAnimationFrame(this.bindFrame);
      this.syncFrame = this.bindFrame = null;

      instances.delete(this);
      if (instances.size === 0) {
        document.body.classList.remove('mrd-has-sticky-atc');
        document.documentElement.style.removeProperty('--mrd-sticky-atc-height');
      }
    }

    /* ---------- Binding to the main product form ---------- */

    findProductInfo() {
      return Array.from(document.querySelectorAll('product-info[id^="MainProduct-"]')).find(
        (element) =>
          element.dataset.productId === this.dataset.productId &&
          element.style.display !== 'none' &&
          !element.closest('quick-add-modal, .quick-add-modal')
      );
    }

    bind() {
      if (!this.active) return;
      const productInfo = this.findProductInfo();
      const source = productInfo?.querySelector('product-form button[type="submit"][name="add"]') || null;

      if (productInfo === this.productInfo && source === this.source) {
        this.syncAll();
        this.measure();
        return;
      }

      this.unbindSource();
      this.productInfo = productInfo || null;
      this.source = source;

      if (!source) {
        this.sourceAbove = false;
        this.updateVisibility();
        return;
      }

      this.sectionId = productInfo.dataset.section;
      this.productForm = source.closest('product-form');

      const schedule = () => this.scheduleSync();

      // Button label, disabled, loading.
      this.sourceObserver = new MutationObserver(schedule);
      this.sourceObserver.observe(source, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
        characterData: true,
      });

      // Price, errors and re-rendered variant pickers.
      this.infoObserver = new MutationObserver(schedule);
      const price = productInfo.querySelector(`#price-${this.sectionId}`);
      if (price) {
        this.infoObserver.observe(price, { attributes: true, childList: true, subtree: true, characterData: true });
      }
      const errorWrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
      if (errorWrapper) {
        this.infoObserver.observe(errorWrapper, { attributes: true, childList: true, subtree: true, characterData: true });
      }
      const pickerParent = productInfo.querySelector('variant-selects')?.parentElement;
      if (pickerParent) this.infoObserver.observe(pickerParent, { childList: true });

      productInfo.addEventListener('change', this.onFormChange);

      this.intersectionObserver = new IntersectionObserver(this.onIntersect, { threshold: 0 });
      this.intersectionObserver.observe(source);

      this.syncAll();
      this.measure();
    }

    unbindSource() {
      this.sourceObserver?.disconnect();
      this.infoObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.productInfo?.removeEventListener('change', this.onFormChange);
      this.sourceObserver = this.infoObserver = this.intersectionObserver = null;
    }

    scheduleBind() {
      cancelAnimationFrame(this.bindFrame);
      this.bindFrame = requestAnimationFrame(() => this.bind());
    }

    scheduleSync() {
      if (this.syncFrame) return;
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        if (!this.active) return;
        if (!this.source?.isConnected || !this.productInfo?.isConnected) {
          this.bind();
          return;
        }
        this.syncAll();
      });
    }

    onProductInfoLoaded() {
      this.scheduleBind();
    }

    onFormChange() {
      this.scheduleSync();
    }

    onVariantChange({ data } = {}) {
      if (!data || data.sectionId !== this.sectionId) return;
      this.updateImage(data.variant);
      this.scheduleSync();
    }

    onMediaChange() {
      this.updateVisibility();
      this.updateHeight();
    }

    /* ---------- Visibility ---------- */

    // Position is measured on every scroll/resize event (passive; browsers already fire scroll at
    // most once per frame), not only on IntersectionObserver crossings: a fast fling, "back to
    // top", an anchor jump or scroll restoration can move the button from below the viewport to
    // above it without it ever intersecting, and an observer alone would then miss the change.
    onScroll() {
      this.measure();
    }

    onIntersect() {
      this.measure();
    }

    measure() {
      if (!this.active) return;
      if (this.source && !this.source.isConnected) {
        this.scheduleBind();
        return;
      }
      const rect = this.source?.getBoundingClientRect();
      const rendered = Boolean(rect && (rect.width > 0 || rect.height > 0));
      this.sourceAbove = rendered && rect.bottom <= 0;
      this.updateVisibility();
    }

    enabledForViewport() {
      return this.mediaQuery.matches
        ? !this.classList.contains('mrd-sticky-atc--hide-desktop')
        : !this.classList.contains('mrd-sticky-atc--hide-mobile');
    }

    updateVisibility() {
      const show = Boolean(this.source && this.sourceAbove && this.enabledForViewport());
      if (show === this.isShown) return;
      this.isShown = show;

      if (!show) {
        this.closeList(false);
        if (this.contains(document.activeElement)) document.activeElement.blur();
      }

      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      this.toggleAttribute('inert', !show);
    }

    updateHeight() {
      const height = this.enabledForViewport() ? Math.ceil(this.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--mrd-sticky-atc-height', `${height}px`);
    }

    /* ---------- Mirroring the main form ---------- */

    syncAll() {
      if (!this.source) return;
      this.syncButton();
      this.syncPrice();
      this.syncOptions();
      this.syncError();
    }

    syncButton() {
      const source = this.source;
      const disabled = source.disabled;
      const busy = source.getAttribute('aria-disabled') === 'true' || source.classList.contains('loading');
      const text = cleanText(source.querySelector('span')) || cleanText(source);

      this.button.disabled = disabled;
      this.button.classList.toggle('is-loading', busy && !disabled);
      this.button.setAttribute('aria-busy', String(busy && !disabled));
      if (this.label.textContent !== text) this.label.textContent = text;
      this.button.classList.toggle('has-price', !disabled && Boolean(this.buttonPrice.textContent));
    }

    syncPrice() {
      const container = this.productInfo.querySelector(`#price-${this.sectionId}`);
      const price = container?.querySelector('.price');

      if (!price || container.classList.contains('hidden')) {
        this.priceWrap.hidden = true;
        this.buttonPrice.textContent = '';
        this.button.classList.remove('has-price');
        return;
      }

      const onSale = price.classList.contains('price--on-sale');
      const current = cleanText(
        onSale
          ? price.querySelector('.price__sale .price-item--last')
          : price.querySelector('.price__regular .price-item--regular')
      );
      const compare = onSale ? cleanText(price.querySelector('.price__sale s.price-item--regular')) : '';

      this.priceWrap.hidden = !current;
      if (this.priceCurrent.textContent !== current) this.priceCurrent.textContent = current;
      this.priceCompare.textContent = compare;
      this.priceCompare.hidden = !compare;
      this.priceWrap.classList.toggle('is-on-sale', Boolean(compare));

      this.buttonPrice.textContent = current;
      this.button.classList.toggle('has-price', !this.button.disabled && Boolean(current));
    }

    getVariantSelects() {
      return Array.from(this.productInfo.querySelectorAll('variant-selects')).find(
        (element) => element.style.display !== 'none'
      );
    }

    getOptionGroup() {
      const picker = this.getVariantSelects();
      if (!picker) return null;

      const groups = Array.from(picker.querySelectorAll('fieldset, .product-form__input--dropdown')).map((element) => {
        const select = element.querySelector('select');
        const radios = Array.from(element.querySelectorAll('input[type="radio"]'));
        const name =
          (select?.querySelector('option[data-option-name]') || radios[0])?.dataset.optionName ||
          cleanText(element.querySelector('legend, label')?.firstChild) ||
          '';
        return { element, select, radios, name: name.replace(/:\s*$/, '') };
      });

      const usable = groups.filter((group) =>
        group.select ? group.select.querySelectorAll('option[data-option-value-id]').length > 1 : group.radios.length > 1
      );

      return (
        usable.find(
          ({ element }) =>
            element.matches('.mereldon-size-buttons, .mereldon-size-picker') ||
            Boolean(element.querySelector('[data-mereldon-size-select]'))
        ) ||
        usable.find(({ name }) => /size/i.test(name)) ||
        usable[0] ||
        null
      );
    }

    syncOptions() {
      const group = this.getOptionGroup();
      this.optionGroup = group;

      if (!group) {
        this.closeList(false);
        this.optionWrap.hidden = true;
        this.classList.remove('has-option');
        return;
      }

      const options = group.select
        ? Array.from(group.select.querySelectorAll('option[data-option-value-id]')).map((option) => ({
            value: option.value,
            unavailable: cleanText(option) !== option.value.trim(),
            selected: option.selected,
          }))
        : group.radios.map((radio) => ({
            value: radio.value,
            unavailable: radio.classList.contains('disabled'),
            selected: radio.checked,
          }));

      const signature = JSON.stringify(options);
      if (signature !== this.optionSignature) {
        this.optionSignature = signature;
        this.renderList(options);
      }

      const selected = options.find((option) => option.selected);
      if (this.current.textContent !== (selected?.value || '')) this.current.textContent = selected?.value || '';

      this.optionLabel.textContent = group.name;
      this.list.setAttribute('aria-label', group.name);
      this.optionWrap.hidden = false;
      this.classList.add('has-option');
    }

    renderList(options) {
      const soldOut = this.dataset.soldOutLabel || 'Sold out';
      const items = options.map(({ value, unavailable, selected }, index) => {
        const item = document.createElement('li');
        item.id = `${this.list.id}-${index}`;
        item.className = 'mrd-sticky-atc__list-item';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(selected));
        item.dataset.value = value;
        item.classList.toggle('is-unavailable', unavailable);

        const text = document.createElement('span');
        text.className = 'mrd-sticky-atc__list-value';
        text.textContent = value;
        item.append(text);

        if (unavailable) {
          const note = document.createElement('span');
          note.className = 'mrd-sticky-atc__list-note';
          note.textContent = soldOut;
          item.append(note);
        }
        return item;
      });

      this.list.replaceChildren(...items);
      if (this.listOpen) {
        const selectedIndex = options.findIndex((option) => option.selected);
        this.setActive(selectedIndex >= 0 ? selectedIndex : Math.max(this.activeIndex, 0));
      }
    }

    /* ---------- Size menu (opens upwards, like Princess Polly) ---------- */

    getItems() {
      return Array.from(this.list.querySelectorAll('[role="option"]'));
    }

    openList() {
      if (this.listOpen || this.optionWrap.hidden || !this.isShown) return;
      const items = this.getItems();
      if (!items.length) return;

      this.listOpen = true;
      this.list.hidden = false;
      this.optionWrap.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      const selectedIndex = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
      this.setActive(selectedIndex >= 0 ? selectedIndex : 0);
      this.list.focus({ preventScroll: true });
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
    }

    closeList(returnFocus) {
      if (!this.listOpen) return;
      this.listOpen = false;
      this.list.hidden = true;
      this.optionWrap.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.list.removeAttribute('aria-activedescendant');
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
      if (returnFocus && this.isShown) this.toggle.focus({ preventScroll: true });
    }

    setActive(index) {
      const items = this.getItems();
      if (!items.length) return;
      const next = Math.max(0, Math.min(index, items.length - 1));
      items.forEach((item, position) => item.classList.toggle('is-active', position === next));
      this.activeIndex = next;
      this.list.setAttribute('aria-activedescendant', items[next].id);

      // Keep the active option visible inside the list without ever scrolling the page.
      const item = items[next];
      if (item.offsetTop < this.list.scrollTop) {
        this.list.scrollTop = item.offsetTop;
      } else if (item.offsetTop + item.offsetHeight > this.list.scrollTop + this.list.clientHeight) {
        this.list.scrollTop = item.offsetTop + item.offsetHeight - this.list.clientHeight;
      }
    }

    onToggleClick() {
      if (this.listOpen) {
        this.closeList(true);
      } else {
        this.openList();
      }
    }

    onToggleKeydown(event) {
      if (['ArrowUp', 'ArrowDown', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        this.openList();
      }
    }

    onListKeydown(event) {
      const items = this.getItems();
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.setActive(this.activeIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setActive(this.activeIndex - 1);
          break;
        case 'Home':
        case 'PageUp':
          event.preventDefault();
          this.setActive(0);
          break;
        case 'End':
        case 'PageDown':
          event.preventDefault();
          this.setActive(items.length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.chooseItem(items[this.activeIndex]);
          break;
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          this.closeList(true);
          break;
        case 'Tab':
          this.closeList(false);
          break;
        default:
          break;
      }
    }

    onListClick(event) {
      const item = event.target.closest('[role="option"]');
      if (item && this.list.contains(item)) this.chooseItem(item);
    }

    onDocumentPointerDown(event) {
      if (!this.optionWrap.contains(event.target)) this.closeList(false);
    }

    onOptionFocusOut(event) {
      if (this.listOpen && event.relatedTarget && !this.optionWrap.contains(event.relatedTarget)) {
        this.closeList(false);
      }
    }

    chooseItem(item) {
      if (!item) return;
      const value = item.dataset.value;
      this.closeList(true);
      this.chooseValue(value);
    }

    syncError() {
      const wrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
      const message =
        wrapper && !wrapper.hidden ? cleanText(wrapper.querySelector('.product-form__error-message')) : '';
      if (this.errorEl.textContent !== message) this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    updateImage(variant) {
      const src = variant?.featured_media?.preview_image?.src;
      if (!this.image || !src) return;
      try {
        const url = new URL(src, window.location.href);
        url.searchParams.set('width', '120');
        this.image.removeAttribute('srcset');
        this.image.src = url.toString();
      } catch (error) {
        /* keep the current image */
      }
    }

    /* ---------- User actions ---------- */

    // Tell product-info that this change came from the fixed bar.
    // Dawn must still update price, inventory, URL and media, but it must not
    // focus the off-screen main picker or scroll the page to the image.
    markStickyVariantChange() {
      if (!this.productInfo?.isConnected) return;
      this.productInfo.dataset.mereldonStickyVariantChange = 'true';
    }

    chooseValue(value) {
      const group = this.optionGroup;
      if (!group || !group.element.isConnected) {
        this.scheduleSync();
        return;
      }

      if (group.select) {
        if (group.select.value === value) return;
        this.markStickyVariantChange();
        group.select.value = value;
        group.select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const radio = group.radios.find((input) => input.value === value);
      if (!radio || radio.checked) return;
      this.markStickyVariantChange();
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onClick() {
      if (!this.source?.isConnected) {
        this.bind();
        return;
      }
      if (this.source.disabled || this.source.getAttribute('aria-disabled') === 'true') return;
      this.source.click();
    }
  }

  customElements.define('mereldon-sticky-atc', MereldonStickyAtc);
})();
